import { useEffect, useRef } from 'react';
import { useNativeBridge } from '@/lib/native-context';
import { useCompanySettings } from '@/hooks/use-settings';
import { hasActivePlatformAdminTenantViewAccess } from '@/lib/admin-tenant-view';
import { isPlatformAdminMode } from '@/lib/platform-admin';

const DEFAULT_TITLE = 'Gesty';
const PLATFORM_ADMIN_TITLE = 'Gesty Admin';

/**
 * Pushes the tenant's branding (logo + company name) into the OS
 * window chrome so the desktop / browser tab reflects whose ERP this
 * is at a glance.
 *
 * - Logo → title-bar icon + Alt-Tab thumbnail (desktop) / favicon (web)
 * - Company name → window title (desktop) / `document.title` (web)
 *
 * The .exe icon (Start menu pin, installer artwork) stays as the
 * Gesty default — it's a Win32 resource baked at build time and
 * cannot be replaced post-install without a rebuild + reinstall.
 *
 * Designed to be mounted ONCE near the auth root. Re-applies when the
 * stored values change (logo replacement, company-name edit, tenant
 * switch). Each side-effect is idempotent: we track last-applied
 * values to avoid redundant Tauri/IPC roundtrips on every render.
 *
 * Failure mode: if the logo URL is unreachable or the response is not
 * a recognisable image, we silently keep the previous icon — never
 * blank. Better to fall back than to flash a missing-icon glyph.
 */
export function useTenantWindowIcon() {
  const bridge = useNativeBridge();
  const platformAdminMode =
    isPlatformAdminMode() && !hasActivePlatformAdminTenantViewAccess();
  const settings = useCompanySettings(!platformAdminMode);
  const lastIcon = useRef<string | null>(null);
  const lastTitle = useRef<string | null>(null);

  // Logo → window icon.
  useEffect(() => {
    if (platformAdminMode) return;
    const logoUrl = settings.data?.logoUrl ?? null;
    if (!logoUrl || logoUrl === lastIcon.current) return;
    let cancelled = false;
    (async () => {
      try {
        await bridge.windowBranding.setIconFromUrl(logoUrl);
        if (!cancelled) lastIcon.current = logoUrl;
      } catch (err) {
        console.warn('[branding] icon update failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [platformAdminMode, settings.data?.logoUrl, bridge.windowBranding]);

  // Company name → window title. Falls back to "Gesty" if no name
  // is configured yet, so a freshly-onboarded tenant doesn't see an
  // empty title bar.
  useEffect(() => {
    if (platformAdminMode) {
      if (lastTitle.current === PLATFORM_ADMIN_TITLE) return;
      void bridge.windowBranding
        .setTitle(PLATFORM_ADMIN_TITLE)
        .then(() => {
          lastTitle.current = PLATFORM_ADMIN_TITLE;
        })
        .catch((err) => console.warn('[branding] title update failed', err));
      return;
    }
    const name = settings.data?.companyName?.trim();
    const title = name && name.length > 0 ? name : DEFAULT_TITLE;
    if (title === lastTitle.current) return;
    void bridge.windowBranding
      .setTitle(title)
      .then(() => {
        lastTitle.current = title;
      })
      .catch((err) => console.warn('[branding] title update failed', err));
  }, [platformAdminMode, settings.data?.companyName, bridge.windowBranding]);
}
